import { type ComponentProps, type ReactNode } from 'react'
import { LoaderCircle } from 'lucide-react'

import { apiErrorMessage } from '@/lib/api-error-message'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/ui/alert-dialog'
import { Button } from '@/ui/button'
import { DialogFooter } from '@/ui/dialog'

type ConfirmMutation = {
  isPending: boolean
  isError: boolean
  error: unknown
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  details,
  confirmLabel,
  confirmVariant = 'destructive',
  confirmDisabled = false,
  pendingLabel,
  mutation,
  onConfirm,
  finalFocus,
  errorMessage = `Couldn’t ${confirmLabel.toLowerCase()}. Try again.`,
  errorCodes,
}: {
  open: boolean
  onOpenChange: (next: boolean) => void
  title: ReactNode
  description: ReactNode
  details?: ReactNode
  confirmLabel: string
  confirmVariant?: 'destructive' | 'default'
  confirmDisabled?: boolean
  pendingLabel: string
  mutation: ConfirmMutation
  onConfirm: () => void
  finalFocus?: ComponentProps<typeof AlertDialogContent>['finalFocus']
  errorMessage?: string | ((error: unknown) => string)
  errorCodes?: Readonly<Record<string, string>>
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent finalFocus={finalFocus}>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
        {details}
        {mutation.isError ? (
          <p role="alert" className="text-destructive">
            {typeof errorMessage === 'function'
              ? errorMessage(mutation.error)
              : apiErrorMessage(mutation.error, errorMessage, { codes: errorCodes })}
          </p>
        ) : null}
        <DialogFooter divider={false}>
          <Button
            variant="outline"
            size="sm"
            disabled={mutation.isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            size="sm"
            disabled={mutation.isPending || confirmDisabled}
            onClick={onConfirm}
          >
            {mutation.isPending ? pendingLabel : confirmLabel}
            {mutation.isPending && (
              <LoaderCircle
                data-slot="button-spinner"
                aria-hidden
                className="animate-spin"
              />
            )}
          </Button>
        </DialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
